import {
  LayoutTemplate,
  Gauge,
  Smartphone,
  Route as RouteIcon,
  MousePointerClick,
  SearchX,
} from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Fase 5B — Hub de rediseño web (Website Strategy + Search + Conversion + Development).
 * URL nueva. No sustituye a /servicios/seo-tecnico (señales técnicas de búsqueda)
 * ni a /servicios/rebranding-seo (migración y redirecciones).
 */
export const websiteRedesignPage: MoneyPageContent = {
  id: "website-redesign",
  icon: LayoutTemplate,
  serviceType: "Website Strategy, Redesign and Development",
  esPath: "/servicios/rediseno-web",
  enPath: "/en/services/website-redesign",
  es: {
    metaTitle: "Rediseño web: estrategia, búsqueda, conversión y desarrollo",
    metaDescription:
      "Rediseño de sitios web para empresas establecidas: arquitectura de información, preservación de SEO, plan de redirecciones, rendimiento, accesibilidad y conversión.",
    keywords: [
      "rediseño web",
      "rediseño de sitio web empresa",
      "arquitectura de información",
      "rediseño web sin perder seo",
      "desarrollo web a medida",
    ],
    h1: "Rediseño web: cuando el sitio deja de sostener la operación comercial",
    breadcrumbLabel: "Rediseño web",
    heroSubtitle: "Estrategia, arquitectura, búsqueda, conversión y desarrollo en un mismo proyecto",
    heroDescription:
      "Un rediseño no es cambiar la apariencia del sitio. Es revisar qué debe explicar el sitio, en qué orden, para quién, con qué evidencia y con qué siguiente paso, sin perder el posicionamiento que ya existe. Trabajo el proyecto completo: diagnóstico, arquitectura de información, contenido, preservación de SEO, plan de redirecciones, rendimiento, accesibilidad, medición y desarrollo.",
    ctaPrimary: { label: "Evaluar mi sitio actual", to: "/contacto" },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Un rediseño es una decisión comercial",
        text: "Se justifica cuando el sitio no explica la oferta, no genera consultas calificadas o no puede sostener el crecimiento.",
      },
      {
        label: "El SEO existente se protege, no se rehace",
        text: "Inventario previo, mapeo de URLs y redirecciones planificadas antes del lanzamiento.",
      },
      {
        label: "Arquitectura antes que diseño visual",
        text: "Qué páginas existen, qué pregunta responde cada una y cómo se enlazan entre sí.",
      },
      {
        label: "Se mide después del lanzamiento",
        text: "Analítica y eventos configurados para saber qué páginas producen contacto real.",
      },
    ],
    explanation: {
      title: "Qué es un rediseño web bien planteado",
      definition:
        "Un rediseño web es la reconstrucción de la estructura, el contenido, la experiencia y la implementación técnica de un sitio para que sostenga los objetivos comerciales actuales de la empresa. Incluye decisiones de arquitectura de información, contenido, conversión, rendimiento, accesibilidad y desarrollo, además de la preservación de las señales de búsqueda que el sitio ya tenía.",
      whyImportant: {
        title: "Por qué los sitios dejan de funcionar",
        text: "Crecen por acumulación: páginas añadidas sin criterio, servicios que ya no se ofrecen, mensajes de hace cinco años, plantillas que ya no se pueden mantener y una estructura que ni el equipo interno sabe explicar.",
      },
      consequences: {
        title: "Qué pasa cuando se rediseña sin plan",
        text: "Se pierde el contenido que sí traía tráfico, desaparecen URLs sin redirección, el sitio nuevo se ve mejor y convierte peor, y nadie puede demostrar qué cambió porque no había medición previa.",
      },
    },
    problems: {
      title: "Por qué un sitio deja de rendir",
      subtitle: "Señales que suelen justificar un rediseño en lugar de un ajuste",
      items: [
        {
          icon: SearchX,
          title: "El sitio no explica la oferta",
          description:
            "El visitante no entiende qué hace la empresa, para quién, con qué alcance ni cuál es el siguiente paso.",
          impact: "Tráfico que entra y sale sin dejar una consulta.",
        },
        {
          icon: RouteIcon,
          title: "Arquitectura de información acumulada",
          description:
            "Menús largos, páginas duplicadas, secciones huérfanas y contenido que compite consigo mismo.",
          impact: "Usuarios perdidos y buscadores que no identifican la página principal de cada tema.",
        },
        {
          icon: Gauge,
          title: "Rendimiento y base técnica agotada",
          description:
            "Plantillas y plugins acumulados, tiempos de carga altos y un sitio que nadie quiere tocar por miedo a romperlo.",
          impact: "Coste de mantenimiento creciente y experiencia lenta, sobre todo en móvil.",
        },
        {
          icon: Smartphone,
          title: "Experiencia móvil de segunda",
          description:
            "Diseño pensado para escritorio y adaptado a la fuerza, con formularios y navegación difíciles en pantalla pequeña.",
          impact: "Se pierde la mayor parte del tráfico real justo en el momento de contacto.",
        },
        {
          icon: MousePointerClick,
          title: "Sin arquitectura de conversión ni medición",
          description:
            "Un único formulario de contacto genérico, sin rutas por intención y sin eventos configurados.",
          impact: "No se sabe qué página genera negocio, así que no se puede priorizar nada.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo un rediseño",
      subtitle: "Del diagnóstico al lanzamiento, con el SEO protegido en cada etapa",
      steps: [
        {
          number: "01",
          title: "Descubrimiento comercial",
          description:
            "Qué vende la empresa, a quién, con qué ciclo de compra y qué debe conseguir el sitio en términos concretos.",
        },
        {
          number: "02",
          title: "Diagnóstico del sitio actual",
          description:
            "Inventario de URLs, páginas con tráfico y enlaces, contenido reutilizable, deuda técnica y rendimiento real.",
        },
        {
          number: "03",
          title: "Arquitectura de información y contenido",
          description:
            "Mapa de páginas, intención de cada una, jerarquía, enlazado interno y qué contenido se conserva, se reescribe o se retira.",
        },
        {
          number: "04",
          title: "Diseño de experiencia y conversión",
          description:
            "Recorridos por tipo de visitante, jerarquía visual, evidencia, formularios y llamadas a la acción adecuadas a cada intención.",
        },
        {
          number: "05",
          title: "Plan de preservación SEO y redirecciones",
          description:
            "Mapeo 1 a 1 de URLs antiguas y nuevas, canonicals, metadatos, encabezados y validación en staging antes de publicar.",
        },
        {
          number: "06",
          title: "Desarrollo, rendimiento y accesibilidad",
          description:
            "Implementación con HTML semántico, contenido disponible en la carga inicial, rendimiento en móvil, contraste y navegación por teclado.",
        },
        {
          number: "07",
          title: "Lanzamiento, medición y ajuste",
          description:
            "Analítica y eventos configurados, monitoreo de indexación y errores tras el lanzamiento, y ajustes sobre datos reales.",
        },
      ],
      ctaText: "Evaluar mi sitio actual",
      ctaLink: "/contacto",
    },
    scope: {
      includes: {
        title: "Qué incluye el proyecto",
        items: [
          "Diagnóstico del sitio actual e inventario de URLs",
          "Arquitectura de información y mapa de contenido",
          "Diseño de experiencia y arquitectura de conversión",
          "Preservación de SEO y plan de redirecciones 301",
          "Rendimiento y experiencia móvil",
          "Accesibilidad: semántica, contraste, foco y navegación por teclado",
          "Contenido extraíble por buscadores y sistemas de IA en la carga inicial",
          "Analítica, eventos y seguimiento de contactos",
          "Desarrollo e implementación",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Sitios de plantilla al precio más bajo posible",
          "Estrategia de marca desde cero (se trabaja en la página de branding)",
          "Campañas de publicidad de pago",
          "Promesas de posiciones o de volumen de leads tras el lanzamiento",
          "Producción de video o fotografía",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién funciona",
        items: [
          "Empresas establecidas cuyo sitio ya no representa lo que venden",
          "Fabricantes y distribuidores con catálogo y ciclo de venta B2B",
          "Empresas de servicios profesionales que compiten por consultas calificadas",
          "Negocios que rediseñan después de un rebranding",
          "Empresas con tráfico orgánico existente que no quieren perderlo",
        ],
      },
      notFor: {
        title: "Para quién no funciona",
        items: [
          "Quien busca el sitio más barato posible",
          "Proyectos sin contenido ni oferta definida",
          "Quien espera que el rediseño por sí solo genere demanda nueva",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes sobre rediseño web",
      items: [
        {
          question: "¿Necesito un rediseño o un refresco del sitio?",
          answer:
            "Si el problema es estético o de secciones desactualizadas, un refresco suele bastar. Si la estructura, el contenido y la base técnica ya no sostienen la operación comercial, el rediseño se justifica.",
        },
        {
          question: "¿Un rediseño puede dañar mi SEO?",
          answer:
            "Puede, y es el riesgo más común. Se evita con inventario previo, mapeo de URLs, redirecciones planificadas, validación en staging y monitoreo posterior. Si el proyecto implica cambio de dominio, se coordina con el trabajo de migración SEO.",
        },
        {
          question: "¿En qué se diferencia esto del SEO técnico?",
          answer:
            "El SEO técnico trabaja rastreo, indexación, renderizado y señales técnicas de un sitio existente. El rediseño trabaja estructura, experiencia, percepción de marca, conversión y desarrollo, y protege esas señales durante el cambio.",
        },
        {
          question: "¿Cuánto cuesta rediseñar un sitio web?",
          answer:
            "Depende del número de páginas, del contenido a reescribir, de la complejidad funcional y de si hay ecommerce o integraciones. Lo defino con alcance escrito después de revisar el sitio actual.",
        },
        {
          question: "¿Trabajan también el desarrollo o solo la estrategia?",
          answer:
            "Ambos. El proyecto cubre estrategia, arquitectura, contenido y la implementación técnica, incluidos desarrollos a medida cuando el negocio lo requiere.",
        },
      ],
    },
    related: {
      title: "Conexiones",
      subtitle: "Con qué se conecta un rediseño",
      links: [
        {
          label: "SEO técnico",
          description: "Rastreo, indexación y señales técnicas del sitio una vez publicado.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "Rebranding y migraciones SEO",
          description: "Mapeo de URLs y redirecciones cuando cambia el dominio o la estructura.",
          to: "/servicios/rebranding-seo",
        },
        {
          label: "Branding",
          description: "Qué representa la marca antes de decidir cómo funciona digitalmente.",
          to: "/servicios/branding",
        },
        {
          label: "Contenido y conversión",
          description: "Qué debe decir cada página y cómo se convierte la visita en consulta.",
          to: "/servicios/contenido-conversion",
        },
        {
          label: "El Venado Foods: marca, ecommerce y SEO",
          description: "Caso documentado con desarrollo de tienda, pasarela de pago, investigación SEO y SEO local.",
          to: "/casos-de-exito/el-venado-foods-branding-ecommerce",
        },
      ],
    },
    llmSummary:
      "Servicio de rediseño web de Luis ROC (ROC Worldwide Agency LLC): estrategia de sitio, arquitectura de información, experiencia, preservación de SEO, plan de redirecciones, rendimiento, accesibilidad, conversión, analítica y desarrollo. Dirigido a empresas establecidas, fabricantes, distribuidores y servicios profesionales.",
    keyPoints: [
      "Rediseño entendido como estrategia de sitio, búsqueda, conversión y desarrollo",
      "El posicionamiento existente se inventaría y se protege con redirecciones planificadas",
      "Distinto del SEO técnico: aquí se trabajan estructura, experiencia y desarrollo",
      "Incluye rendimiento móvil, accesibilidad y contenido extraíble en la carga inicial",
      "No se prometen posiciones ni volumen de leads tras el lanzamiento",
    ],
  },
  en: {
    metaTitle: "Website Redesign: strategy, search, conversion and development",
    metaDescription:
      "Website redesign for established companies: information architecture, SEO preservation, redirect planning, performance, accessibility, conversion and development.",
    keywords: [
      "website redesign",
      "b2b website redesign",
      "information architecture",
      "website redesign without losing seo",
      "custom website development",
    ],
    h1: "Website redesign: when the site stops supporting the commercial operation",
    breadcrumbLabel: "Website redesign",
    heroSubtitle: "Strategy, architecture, search, conversion and development in a single project",
    heroDescription:
      "A redesign is not a new coat of paint. It is a review of what the site must explain, in what order, for whom, with what evidence and with what next step — without losing the search visibility that already exists. I run the full project: diagnosis, information architecture, content, SEO preservation, redirect planning, performance, accessibility, measurement and development.",
    ctaPrimary: { label: "Request a Website Evaluation", to: "/en/contact" },
    ctaSecondary: { label: "See documented cases", to: "/en/case-studies" },
    summary: [
      {
        label: "A redesign is a commercial decision",
        text: "It is justified when the site fails to explain the offer, fails to produce qualified inquiries, or cannot support growth.",
      },
      {
        label: "Existing SEO is protected, not rebuilt",
        text: "URL inventory, one-to-one mapping and planned redirects before launch, not after it.",
      },
      {
        label: "Architecture before visual design",
        text: "Which pages exist, which question each one answers, and how they link to each other.",
      },
      {
        label: "Measured after launch",
        text: "Analytics and events configured so you know which pages actually produce contact.",
      },
    ],
    explanation: {
      title: "What a properly scoped website redesign is",
      definition:
        "A website redesign rebuilds the structure, content, experience and technical implementation of a site so it supports the company's current commercial goals. It covers information architecture, content, conversion, performance, accessibility and development, plus preservation of the search signals the site already had.",
      whyImportant: {
        title: "Why websites stop performing",
        text: "They grow by accumulation: pages added with no criteria, services no longer offered, messaging from five years ago, templates nobody can maintain, and a structure the internal team can no longer explain.",
      },
      consequences: {
        title: "What happens when a redesign has no plan",
        text: "The content that actually drove traffic disappears, URLs vanish without redirects, the new site looks better and converts worse, and nobody can prove what changed because nothing was measured beforehand.",
      },
    },
    problems: {
      title: "Why a website stops performing",
      subtitle: "Signals that justify a redesign rather than an adjustment",
      items: [
        {
          icon: SearchX,
          title: "The site does not explain the offer",
          description:
            "Visitors cannot tell what the company does, who it serves, at what scope, or what the next step is.",
          impact: "Traffic that arrives and leaves without a single inquiry.",
        },
        {
          icon: RouteIcon,
          title: "Accumulated information architecture",
          description:
            "Long menus, duplicate pages, orphan sections and content competing with itself.",
          impact: "Lost users and search engines that cannot identify the main page for each topic.",
        },
        {
          icon: Gauge,
          title: "Exhausted technical foundation",
          description:
            "Layered templates and plugins, slow load times, and a site nobody wants to touch for fear of breaking it.",
          impact: "Rising maintenance cost and a slow experience, especially on mobile.",
        },
        {
          icon: Smartphone,
          title: "Second-class mobile experience",
          description:
            "Desktop-first design forced into small screens, with navigation and forms that are hard to use.",
          impact: "Most of the real traffic is lost exactly at the point of contact.",
        },
        {
          icon: MousePointerClick,
          title: "No conversion architecture and no measurement",
          description:
            "A single generic contact form, no paths by intent, and no configured events.",
          impact: "Nobody knows which page generates business, so nothing can be prioritized.",
        },
      ],
    },
    methodology: {
      title: "How I run a redesign",
      subtitle: "From diagnosis to launch, with SEO protected at every stage",
      steps: [
        {
          number: "01",
          title: "Commercial discovery",
          description:
            "What the company sells, to whom, with what buying cycle, and what the site must achieve in concrete terms.",
        },
        {
          number: "02",
          title: "Current site diagnosis",
          description:
            "URL inventory, pages with traffic and links, reusable content, technical debt and real performance.",
        },
        {
          number: "03",
          title: "Information architecture and content",
          description:
            "Page map, intent per page, hierarchy, internal linking, and what content is kept, rewritten or retired.",
        },
        {
          number: "04",
          title: "Experience and conversion design",
          description:
            "Journeys by visitor type, visual hierarchy, evidence, forms, and calls to action matched to each intent.",
        },
        {
          number: "05",
          title: "SEO preservation and redirect plan",
          description:
            "One-to-one mapping of old and new URLs, canonicals, metadata, headings, and staging validation before launch.",
        },
        {
          number: "06",
          title: "Development, performance and accessibility",
          description:
            "Semantic HTML, content available on initial load, mobile performance, contrast and keyboard navigation.",
        },
        {
          number: "07",
          title: "Launch, measurement and adjustment",
          description:
            "Analytics and events configured, indexing and error monitoring after launch, and adjustments based on real data.",
        },
      ],
      ctaText: "Request a Website Evaluation",
      ctaLink: "/en/contact",
    },
    scope: {
      includes: {
        title: "What the project includes",
        items: [
          "Current site diagnosis and URL inventory",
          "Information architecture and content map",
          "Experience design and conversion architecture",
          "SEO preservation and 301 redirect planning",
          "Performance and mobile experience",
          "Accessibility: semantics, contrast, focus and keyboard navigation",
          "Content extractable by search engines and AI systems on initial load",
          "Analytics, events and inquiry tracking",
          "Development and implementation",
        ],
      },
      notIncludes: {
        title: "What it does not include",
        items: [
          "Lowest-price template websites",
          "Brand strategy from scratch, which lives on the branding page",
          "Paid advertising campaigns",
          "Promises of rankings or lead volume after launch",
          "Video or photography production",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this works for",
        items: [
          "Established companies whose site no longer represents what they sell",
          "Manufacturers and distributors with a catalog and a B2B sales cycle",
          "Professional service firms competing for qualified inquiries",
          "Companies redesigning after a rebrand",
          "Companies with existing organic traffic they cannot afford to lose",
        ],
      },
      notFor: {
        title: "Who this does not work for",
        items: [
          "Anyone shopping for the cheapest possible website",
          "Projects with no content or defined offer",
          "Anyone expecting the redesign alone to create new demand",
        ],
      },
    },
    faq: {
      title: "Website redesign FAQ",
      items: [
        {
          question: "Do I need a redesign or a refresh?",
          answer:
            "If the problem is visual or a few outdated sections, a refresh is usually enough. If the structure, content and technical foundation no longer support the commercial operation, a redesign is justified.",
        },
        {
          question: "Can a website redesign hurt SEO?",
          answer:
            "It can, and it is the most common risk. It is avoided with a URL inventory, mapping, planned redirects, staging validation and post-launch monitoring. If the project involves a domain change, it is coordinated with the SEO migration work.",
        },
        {
          question: "How is this different from technical SEO?",
          answer:
            "Technical SEO works on crawling, indexing, rendering and technical signals of an existing site. A redesign works on structure, experience, brand perception, conversion and development, and protects those signals through the change.",
        },
        {
          question: "How much does a website redesign cost?",
          answer:
            "It depends on the number of pages, how much content must be rewritten, functional complexity, and whether ecommerce or integrations are involved. I define it with a written scope after reviewing the current site.",
        },
        {
          question: "Do you handle development or only strategy?",
          answer:
            "Both. The project covers strategy, architecture, content and technical implementation, including custom development when the business requires it.",
        },
      ],
    },
    related: {
      title: "Connections",
      subtitle: "What a redesign connects to",
      links: [
        {
          label: "Technical SEO",
          description: "Crawling, indexing and technical signals once the site is live.",
          to: "/en/services/technical-seo",
        },
        {
          label: "Rebranding and SEO migration",
          description: "URL mapping and redirects when the domain or structure changes.",
          to: "/en/services/rebranding-seo",
        },
        {
          label: "Branding",
          description: "What the brand stands for, before deciding how it works digitally.",
          to: "/en/services/branding",
        },
        {
          label: "Content and conversion",
          description: "What each page must say and how a visit becomes an inquiry.",
          to: "/en/services/content-conversion",
        },
        {
          label: "El Venado Foods: brand, ecommerce and SEO",
          description: "Documented case with store development, payment gateway, SEO research and local SEO.",
          to: "/en/case-studies/el-venado-foods-branding-ecommerce",
        },
      ],
    },
    llmSummary:
      "Website redesign consulting by Luis ROC (ROC Worldwide Agency LLC): site strategy, information architecture, experience, SEO preservation, redirect planning, performance, accessibility, conversion, analytics and development. For established companies, manufacturers, distributors and professional services.",
    keyPoints: [
      "Redesign treated as site strategy, search, conversion and development",
      "Existing rankings are inventoried and protected with planned redirects",
      "Different from technical SEO: structure, experience and development live here",
      "Includes mobile performance, accessibility and content extractable on initial load",
      "No rankings or lead volume are promised after launch",
    ],
  },
};
